import { LinkOptions, useLink, usePageContent } from "@appiodev/xcore-client/xcore-ui";
import { Category } from "@appiodev/xcore-core";
import { OptionType } from "design-system/xcore/Select";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { ValueType } from "react-select";
import { useLayout } from "xcore";

export type Filter = readonly [{options: OptionType[]; value: OptionType[]}, (v?: OptionType[]) => string, string];

export const useFilters = (filters: Filter[], trans?: LinkOptions & { shallow?: boolean }) => {
  const { query } = useRouter();
  const [content] = usePageContent();
  const { replace } = useLink();

  const getQuery = (key?: string, values?: OptionType[], page?: number) =>
    prefixQuery(
      ...filters.map(([_, getQ, k]) => k === key ? getQ(values) : getQ()),
      // eslint-disable-next-line @typescript-eslint/no-extra-parens
      page ? `page=${page}` : ""
    );

  const onChange = (key: string) => (values: ValueType<OptionType>) =>
    replace(content, { query: getQuery(key, values ?? [] as any), ...trans });

  return { onChange, nextPage: () => getQuery(undefined, undefined, Number(query.page ?? 1) + 1) };
};

export const useCategoryFilter = (q: string, categories: Category[]) => {
  const { stringify } = useLayout();
  const { query } = useRouter();

  const options = useMemo(
    () => categories.map(c => ({ label: stringify(c.name), value: c.id })),
    [categories, stringify]
  );

  const value = useMemo(() => getValues(query[q] ?? [], options), [options, q, query]);

  const getQuery = (v?: OptionType[]) => (v ?? value).map(o => `${q}=${o.value}`).join("&");

  return [{ options, value }, getQuery, q] as const;
};

export const useValueFilter = (q: string, options: OptionType[]) => {
  const { query } = useRouter();

  const value = useMemo(() => getValues(query[q] ?? [], options), [options, q, query]);

  const getQuery = (v?: OptionType[]) => (v ?? value)
    .map(o => o && `${q}=${o.value}`)
    .filter(Boolean)
    .join("&");

  return [{ options, value }, getQuery, q] as const;
};

const prefixQuery = (...q: string[]) => {
  const query = q.filter(Boolean).join("&");
  return query && "?" + query;
};

const getValues = (query: string | string[], options: OptionType[]) =>
  options.filter(o =>
    typeof query === "string"
      ? query === o.value.toString()
      : query.includes(o.value.toString())
  );
